import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSettings } from '../hooks/useGlobalSettings';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  senha: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const { settings } = useGlobalSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    try {
      const success = await login(data.email, data.senha);
      if (success) {
        if (location.state?.returnUrl) {
          navigate(location.state.returnUrl);
        } else {
          navigate('/dashboard');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-4 font-oswald">
      <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-2xl w-full max-w-5xl overflow-hidden">
        <div className="flex flex-col lg:flex-row min-h-[600px]">
          {/* Lado Esquerdo - Branding */}
          <div className="lg:w-1/2 p-12 flex items-center justify-center relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent opacity-50" />
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse delay-1000" />
            
            <div className="text-center relative z-10">
              <div className="w-32 h-32 bg-white/10 rounded-[2rem] mx-auto mb-8 flex items-center justify-center backdrop-blur-xl border border-white/20 shadow-2xl transform transition-transform group-hover:scale-110 duration-500">
                {settings?.logoUrl ? (
                  <img
                    src={settings.logoUrl}
                    alt="Logo do Sistema"
                    className="w-24 h-24 object-contain"
                  />
                ) : settings?.iconUrl ? (
                  <img
                    src={settings.iconUrl}
                    alt="Ícone do Sistema"
                    className="w-20 h-20 object-contain"
                  />
                ) : (
                  <img
                    src="/favicon.png"
                    alt="Logo"
                    className="w-20 h-20 object-contain"
                  />
                )}
              </div>
              <h1 className="text-5xl font-bold text-white mb-4 tracking-tight uppercase">
                {settings?.pageTitle || 'BChat'}
              </h1>
              <div className="w-12 h-1 bg-blue-500 mx-auto mb-6 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]" />
              <p className="text-xl text-blue-100/80 mb-8 uppercase tracking-[0.2em] font-medium text-xs">
                Gestão de Relacionamento
              </p>
              <p className="text-gray-400 max-w-sm mx-auto leading-relaxed text-sm uppercase tracking-wider opacity-60">
                Acesse sua plataforma de comunicação inteligente e conecte-se com seus clientes de forma estratégica.
              </p>
            </div>
          </div>

          {/* Lado Direito - Form de Login */}
          <div className="lg:w-1/2 p-12 bg-white/5 backdrop-blur-sm border-l border-white/10 flex items-center">
            <div className="w-full max-w-md mx-auto">
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-white mb-2 uppercase tracking-tight">Login</h2>
                <p className="text-gray-400 text-xs uppercase tracking-[0.2em] font-medium">Acesse sua conta para continuar</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] ml-2">
                    E-mail
                  </label>
                  <div className="relative group">
                    <input
                      {...register('email')}
                      type="email"
                      id="email"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                      placeholder="seu@email.com"
                      disabled={isSubmitting}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="senha" className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] ml-2">
                    Senha
                  </label>
                  <div className="relative group">
                    <input
                      {...register('senha')}
                      type="password"
                      id="senha"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                      placeholder="••••••••"
                      disabled={isSubmitting}
                    />
                  </div>
                  {errors.senha && (
                    <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.senha.message}</p>
                  )}
                </div>

                <div className="flex justify-end -mt-4">
                  <Link
                    to="/esqueci-senha"
                    className="text-blue-400/70 hover:text-blue-300 text-[10px] uppercase tracking-[0.15em] font-bold transition-colors"
                  >
                    Esqueci minha senha
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-400/50 relative overflow-hidden group/btn disabled:opacity-50 mt-4"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white"></div>
                      Autenticando...
                    </div>
                  ) : (
                    'Entrar no Sistema'
                  )}
                </button>
              </form>
              
              <div className="mt-8 text-center">
                <p className="text-gray-500 text-[9px] uppercase tracking-[0.3em] font-bold">
                  © {new Date().getFullYear()} {settings?.companyName || 'BChat'} • Todos os direitos reservados
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}